import {Err, Ok, Result} from 'oxide.ts';
import {setBounded} from '../../lib/boundedMap';
import {getEntityVersions, makeSecureApiCall} from '../../rest/api';
import type {EntityType} from '../../@types/entity';
import type {EntityNames} from '../../entities';
import {HttpError, httpErrorMessage} from '../../lib/picos/http';

type CacheEntry<T> = {
    addedOn: number;
    versions: T[];
};

const cache = new Map<string, CacheEntry<any>>();

/**
 * Entry ceiling. The Dashboard activity feed enriches up to 40 entities per
 * pass and re-runs as results arrive, so this sits comfortably above a single
 * feed's working set — evicting inside one pass would only cause the feed to
 * refetch what it just asked for. Each entry holds a full version-history
 * array, which is what makes an unbounded cache expensive here.
 */
const MAX_ENTRIES = 200;

/**
 * Requests that have been sent and not yet answered, keyed by the cache key
 * plus the addedOn asked for. A caller asking for a version that is already on
 * its way waits for that request instead of sending another (OWA-961): the
 * Dashboard activity feed asks again on every re-render, and without this each
 * re-render sent a full new set until the user was rate limited. An entry
 * removes itself when its request settles, so this never holds more than the
 * requests in flight.
 */
const inFlight = new Map<string, Promise<Result<any[], HttpError>>>();

/**
 * Fetch entity versions with in-memory caching.
 * Cache is keyed by entityType-entityId, invalidated when entity.addedOn changes.
 */
export async function getVersionsCached<T>(entityType: EntityNames, entity: EntityType): Promise<Result<T[], HttpError>> {
    return getVersionsWithLoader(entityType, entity, () => getEntityVersions<T>(entityType, entity));
}

/** A feed pass shares the same cache and in-flight slots as single-record readers. */
export async function getVersionsBatchCached(requests: Array<{entityType: EntityNames; entity: EntityType}>): Promise<Array<Result<any[], HttpError>>> {
    const pending: Array<{entityType: EntityNames; id: string; resolve: (result: Result<any[], HttpError>) => void}> = [];
    const results = requests.map(({entityType, entity}) => getVersionsWithLoader<any>(entityType, entity,
        () => new Promise(resolve => pending.push({entityType, id: entity.id, resolve}))));
    // The dashboard caps its pass at 40. Chunk defensively for other callers.
    for (let offset = 0; offset < pending.length; offset += 40) {
        const batch = pending.slice(offset, offset + 40);
        void (async () => {
            try {
                const response = await makeSecureApiCall('/versions/batch', 'POST', {
                    entities: batch.map(({entityType, id}) => ({entityType, id})),
                });
                for (const item of batch) {
                    const versions = response?.body?.[`${item.entityType}-${item.id}`];
                    item.resolve(!response || response.status >= 400
                        ? Err(new HttpError(response?.status ?? 503, httpErrorMessage(response?.status ?? 503, response?.body)))
                        : Array.isArray(versions) ? Ok(versions) : Err(new HttpError(502, 'Invalid version-history response')));
                }
            }
            catch (error) {
                const failure = error instanceof HttpError ? error : new HttpError(503, 'Version histories are unavailable');
                for (const item of batch) item.resolve(Err(failure));
            }
        })();
    }
    return Promise.all(results);
}

async function getVersionsWithLoader<T>(entityType: EntityNames, entity: EntityType, load: () => Promise<Result<T[], HttpError>>): Promise<Result<T[], HttpError>> {
    const key = `${entityType}-${entity.id}`;
    const cached = cache.get(key);

    if (cached && cached.addedOn === entity.addedOn) {
        return Ok(cached.versions);
    }

    const slot = `${key}@${entity.addedOn}`;
    const pending = inFlight.get(slot);

    if (pending) return pending;

    const request = load();
    inFlight.set(slot, request);

    let result: Result<T[], HttpError>;

    try {
        result = await request;
    }
    catch (err) {
        if (inFlight.get(slot) === request) inFlight.delete(slot);
        throw err;
    }

    // A request whose slot was dropped by an invalidation is answering a
    // question nobody is asking now: it must not touch the slot or the cache.
    if (inFlight.get(slot) !== request) return result;
    inFlight.delete(slot);

    // An older version that settles late must not replace a newer cached one.
    const newerCached = (cache.get(key)?.addedOn ?? -Infinity) > entity.addedOn;

    if (result.isOk() && !newerCached) {
        setBounded(cache, key, {addedOn: entity.addedOn, versions: result.unwrap()}, MAX_ENTRIES);
    }

    return result;
}

/**
 * Invalidate the cache for a specific entity.
 */
export function invalidateVersionCache(entityType: string, entityId: string): void {
    const key = `${entityType}-${entityId}`;
    cache.delete(key);

    for (const slot of inFlight.keys()) {
        if (slot.startsWith(`${key}@`)) inFlight.delete(slot);
    }
}

/**
 * Clear the entire version cache.
 */
export function clearVersionCache(): void {
    cache.clear();
    inFlight.clear();
}
